import { useState } from 'react'
import type { FormEvent } from 'react'
import { useSettings, useUpdateSettings } from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { CheckboxField, TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatDateTime, trimDecimals } from '@/lib/format'
import { OperatingDaysCard } from './OperatingDaysCard'
import styles from './SettingsPage.module.css'

/**
 * A16 — Tenant settings.
 *
 * *** THESE VALUES HAVE TEETH ***
 * Every field here changes what students and mentors can actually do, and two
 * of them can break booking outright:
 *
 *   join_window_minutes  — zero means the Join button never enables, and
 *                          nobody can attend a session they paid for.
 *   payment_hold_minutes — too short cancels bookings mid-checkout; too long
 *                          freezes a mentor's calendar with abandoned carts.
 *
 * The server enforces bounds regardless of what this form sends. The hints
 * exist so an admin understands the consequence BEFORE hitting a validation
 * error, rather than learning the rule by being refused.
 */
export function SettingsPage() {
  const toast = useToast()
  const { data, isLoading, error } = useSettings()
  const updateMutation = useUpdateSettings()
  const [dirty, setDirty] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const number = (key: string) => Number(form.get(key) ?? 0)

    try {
      await updateMutation.mutateAsync({
        payment_hold_minutes: number('payment_hold_minutes'),
        min_booking_lead_minutes: number('min_booking_lead_minutes'),
        max_booking_days_ahead: number('max_booking_days_ahead'),
        cancellation_cutoff_hours: number('cancellation_cutoff_hours'),
        allow_cancel_after_cutoff:
          form.get('allow_cancel_after_cutoff') === 'on',
        student_can_cancel: form.get('student_can_cancel') === 'on',
        show_ratings_to_students: form.get('show_ratings_to_students') === 'on',
        show_ratings_to_mentors: form.get('show_ratings_to_mentors') === 'on',
        mentor_can_cancel: form.get('mentor_can_cancel') === 'on',
        // Percentages stay strings — same reason as money.
        refund_percent_before: String(form.get('refund_percent_before') ?? '0'),
        refund_percent_after: String(form.get('refund_percent_after') ?? '0'),
        join_window_minutes: number('join_window_minutes'),
        join_grace_minutes: number('join_grace_minutes'),
        student_reminder_minutes: number('student_reminder_minutes'),
        max_attachment_mb: String(form.get('max_attachment_mb') ?? '10'),
      })
      toast.success('Settings saved.')
      setDirty(false)
    } catch (caught) {
      // The server's message names the offending field and its allowed range,
      // so it is far more useful than anything generic.
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save settings.',
      )
    }
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Settings" />
        <Card>
          <p className={styles.muted}>Loading…</p>
        </Card>
      </>
    )
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Settings" />
        <Card>
          <p className={styles.error}>{error?.message ?? 'Could not load settings.'}</p>
        </Card>
      </>
    )
  }

  return (
    <PageStack>
      <PageHeader
        title="Settings"
        description="How booking, cancellation, and joining work for your organisation."
      />

      {/*
        OUTSIDE the form on purpose. Closing a day needs its own confirmation
        showing which sessions are affected, and that cannot ride along with a
        Save that writes eleven other fields at once. Keeping it out also
        stops these checkboxes joining the form's submission.
      */}
      <OperatingDaysCard settings={data} />

      <form onSubmit={handleSubmit} onChange={() => setDirty(true)}>
        <div className={styles.sections}>
          <Card
            title="Booking window"
            description="When students can book, and how far ahead."
          >
            <div className={styles.grid}>
              <TextField
                label="Minimum notice"
                name="min_booking_lead_minutes"
                type="number"
                min={0}
                max={10080}
                defaultValue={String(data.min_booking_lead_minutes)}
                hint="Minutes before a slot that booking closes. Gives mentors time to prepare."
              />

              <TextField
                label="Book up to"
                name="max_booking_days_ahead"
                type="number"
                min={1}
                max={365}
                defaultValue={String(data.max_booking_days_ahead)}
                hint="Days into the future students can see slots."
              />

              <TextField
                label="Payment hold"
                name="payment_hold_minutes"
                type="number"
                min={1}
                max={120}
                defaultValue={String(data.payment_hold_minutes)}
                hint="Minutes a slot stays reserved during checkout. Too short cancels people mid-payment; too long blocks the slot for everyone else."
              />
            </div>
          </Card>

          <Card
            title="Joining a session"
            description="When the Join button becomes available."
          >
            <div className={styles.grid}>
              <TextField
                label="Opens before start"
                name="join_window_minutes"
                type="number"
                min={1}
                max={60}
                defaultValue={String(data.join_window_minutes)}
                hint="Minutes before the session that both parties can join."
              />

              <TextField
                label="Stays open for"
                name="join_grace_minutes"
                type="number"
                min={0}
                max={240}
                defaultValue={String(data.join_grace_minutes)}
                hint="Minutes after the start time that late arrivals can still join."
              />
            </div>

            <div className={styles.calloutSpacing}>
              <Callout tone="info">
                <p>
                  The meeting link is never shown in the app or in an email — it
                  is resolved only when someone taps Join inside this window.
                  That is what keeps a session private to the two people in it.
                </p>
              </Callout>
            </div>
          </Card>

          <Card
            title="Cancellation and refunds"
            description="Who can cancel, and what gets returned."
          >
            <div className={styles.grid}>
              <TextField
                label="Free cancellation until"
                name="cancellation_cutoff_hours"
                type="number"
                min={0}
                max={720}
                defaultValue={String(data.cancellation_cutoff_hours)}
                hint="Hours before the session. Cancelling earlier than this gets the higher refund below."
              />

              <TextField
                label="Refund before cutoff"
                name="refund_percent_before"
                type="number"
                min={0}
                max={100}
                step="0.01"
                defaultValue={trimDecimals(data.refund_percent_before)}
                hint="Percent returned when cancelled in good time."
              />

              <TextField
                label="Refund after cutoff"
                name="refund_percent_after"
                type="number"
                min={0}
                max={100}
                step="0.01"
                defaultValue={trimDecimals(data.refund_percent_after)}
                hint="Percent returned for a late cancellation."
              />
            </div>

            <div className={styles.checkboxes}>
              <CheckboxField
                label="Allow cancelling after the cutoff"
                name="allow_cancel_after_cutoff"
                defaultChecked={data.allow_cancel_after_cutoff}
                hint={
                  data.allow_cancel_after_cutoff
                    ? 'Late cancellations go through and get the “after cutoff” refund above.'
                    : 'Late cancellations are refused, so the “after cutoff” refund never applies.'
                }
              />
              <CheckboxField
                label="Students can cancel their own bookings"
                name="student_can_cancel"
                defaultChecked={data.student_can_cancel}
                hint="Turning this off means every cancellation goes through support."
              />

              <CheckboxField
                label="Mentors can cancel bookings"
                name="mentor_can_cancel"
                defaultChecked={data.mentor_can_cancel}
                hint="Mentors sometimes must — illness, emergencies. The student is refunded and notified."
              />
            </div>
          </Card>

          <Card title="Ratings">
            <div className={styles.grid}>
              <CheckboxField
                label="Show mentor ratings to students"
                name="show_ratings_to_students"
                defaultChecked={data.show_ratings_to_students}
                hint={
                  data.show_ratings_to_students
                    ? 'Students see a mentor’s score on their card and profile.'
                    : 'Students still rate their sessions — the score is simply not displayed anywhere in the app.'
                }
              />

              <CheckboxField
                label="Let mentors see their own rating"
                name="show_ratings_to_mentors"
                defaultChecked={data.show_ratings_to_mentors}
                hint={
                  data.show_ratings_to_mentors
                    ? 'Mentors see their own score on their profile.'
                    : 'The score stays between the student and you. In a small group a mentor who sees a low score can work out who gave it.'
                }
              />
            </div>
          </Card>

          <Card title="Notifications and files">
            <div className={styles.grid}>
              <TextField
                label="Student reminder"
                name="student_reminder_minutes"
                type="number"
                min={1}
                max={10080}
                defaultValue={String(data.student_reminder_minutes)}
                hint="Minutes before a session. Mentors set their own lead time individually."
              />

              <TextField
                label="Attachment limit"
                name="max_attachment_mb"
                type="number"
                min={0.1}
                max={100}
                step="0.1"
                defaultValue={trimDecimals(data.max_attachment_mb)}
                hint="Megabytes per file in chat."
              />
            </div>
          </Card>
        </div>

        <div className={styles.saveBar}>
          <span className={styles.savedNote}>
            {dirty
              ? 'You have unsaved changes.'
              : data.updated_at
                ? `Last saved ${formatDateTime(data.updated_at)}`
                : ''}
          </span>
          <Button
            type="submit"
            variant="primary"
            loading={updateMutation.isPending}
            disabled={!dirty}
          >
            Save settings
          </Button>
        </div>
      </form>
    </PageStack>
  )
}
